import * as React from 'react';

import { logger } from '@/utils/logger/logger';

interface InfiniteScrollProps {
  isLoading: boolean;
  hasMore: boolean;
  next: () => unknown;
  threshold?: number;
  root?: Element | Document | null;
  rootMargin?: string;
  reverse?: boolean;
  error?: string;
  children?: React.ReactNode;
}

const TAG = 'InfiniteScroll:';

export default function InfiniteScroll({
  isLoading,
  hasMore,
  next,
  threshold = 1,
  root = null,
  rootMargin = '200px',
  error,
  children,
}: InfiniteScrollProps) {
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const observer = React.useRef<IntersectionObserver | null>(null);

  React.useEffect(() => {
    let safeThreshold = threshold;
    if (threshold < 0 || threshold > 1) {
      logger.warn(
        TAG,
        'threshold should be between 0 and 1. You are exceed the range. will use default value: 1'
      );
      safeThreshold = 1;
    }

    // Clean up previous observer
    if (observer.current) {
      observer.current.disconnect();
    }

    // Don't create observer if we're loading, have an error, or no more data
    if (isLoading || error || !hasMore || !sentinelRef.current) {
      return;
    }

    // Create new intersection observer
    observer.current = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading) {
          logger.debug(TAG, 'Sentinel intersected, loading more data...');
          next();
        }
      },
      {
        threshold: safeThreshold,
        root,
        rootMargin,
      }
    );

    observer.current.observe(sentinelRef.current);

    // Cleanup function
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [hasMore, isLoading, next, threshold, root, rootMargin, error]);

  return (
    <>
      {children}
      {/* Sentinel element that triggers infinite scroll */}
      {hasMore && !error && (
        <div ref={sentinelRef} className="h-1 w-full" data-testid="infinite-scroll-sentinel" />
      )}
    </>
  );
}
